/**
 * BulkConnectionsDialog - Dialog para gerenciar conexões em massa
 * Permite vincular/desvincular múltiplas entidades de uma vez
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - selectedIds: array de IDs selecionados
 * - entityType: 'device' | 'group' | 'user' (tipo da entidade base)
 * - entityName: string (nome para exibição, ex: 'dispositivos')
 * - onSuccess: function (callback após sucesso)
 *
 * Exemplo de uso:
 * <BulkConnectionsDialog
 *   open={connectionsDialogOpen}
 *   onClose={() => setConnectionsDialogOpen(false)}
 *   selectedIds={selectedIds}
 *   entityType="device"
 *   entityName="dispositivos"
 *   onSuccess={() => { clearSelection(); refresh(); }}
 * />
 */

import React, {
 memo, useState, useMemo, useCallback 
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PlaceIcon from '@mui/icons-material/Place';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import TerminalIcon from '@mui/icons-material/Terminal';
import BuildIcon from '@mui/icons-material/Build';
import DevicesIcon from '@mui/icons-material/Devices';
import FolderIcon from '@mui/icons-material/Folder';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';

import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';
import { useEffectAsync } from '../../../reactHelper';
import fetchOrThrow from '../../util/fetchOrThrow';
import { formatNotificationTitle } from '../../util/formatter';
import useFeatures from '../../util/useFeatures';

// Configuração de conexões por tipo de entidade
const CONNECTION_CONFIGS = {
  device: [
    { key: 'geofence', label: 'sharedGeofences', icon: PlaceIcon, endpoint: '/api/geofences', keyLink: 'geofenceId', keyBase: 'deviceId' },
    { key: 'notification', label: 'sharedNotifications', icon: NotificationsIcon, endpoint: '/api/notifications', keyLink: 'notificationId', keyBase: 'deviceId', titleGetter: 'notification' },
    {
 key: 'driver', label: 'sharedDrivers', icon: PersonIcon, endpoint: '/api/drivers', keyLink: 'driverId', keyBase: 'deviceId', titleGetter: 'driver', feature: 'disableDrivers' 
},
    {
 key: 'attribute', label: 'sharedComputedAttributes', icon: SettingsIcon, endpoint: '/api/attributes/computed', keyLink: 'attributeId', keyBase: 'deviceId', titleGetter: 'description', feature: 'disableComputedAttributes' 
},
    {
 key: 'command', label: 'sharedSavedCommands', icon: TerminalIcon, endpoint: '/api/commands', keyLink: 'commandId', keyBase: 'deviceId', titleGetter: 'description', feature: 'disableSavedCommands' 
},
    { key: 'maintenance', label: 'sharedMaintenance', icon: BuildIcon, endpoint: '/api/maintenance', keyLink: 'maintenanceId', keyBase: 'deviceId', feature: 'disableMaintenance' },
  ],
  group: [
    { key: 'geofence', label: 'sharedGeofences', icon: PlaceIcon, endpoint: '/api/geofences', keyLink: 'geofenceId', keyBase: 'groupId' },
    { key: 'notification', label: 'sharedNotifications', icon: NotificationsIcon, endpoint: '/api/notifications', keyLink: 'notificationId', keyBase: 'groupId', titleGetter: 'notification' },
    {
 key: 'driver', label: 'sharedDrivers', icon: PersonIcon, endpoint: '/api/drivers', keyLink: 'driverId', keyBase: 'groupId', titleGetter: 'driver', feature: 'disableDrivers' 
},
    {
 key: 'attribute', label: 'sharedComputedAttributes', icon: SettingsIcon, endpoint: '/api/attributes/computed', keyLink: 'attributeId', keyBase: 'groupId', titleGetter: 'description', feature: 'disableComputedAttributes' 
},
    {
 key: 'command', label: 'sharedSavedCommands', icon: TerminalIcon, endpoint: '/api/commands', keyLink: 'commandId', keyBase: 'groupId', titleGetter: 'description', feature: 'disableSavedCommands' 
},
    { key: 'maintenance', label: 'sharedMaintenance', icon: BuildIcon, endpoint: '/api/maintenance', keyLink: 'maintenanceId', keyBase: 'groupId', feature: 'disableMaintenance' },
  ],
  user: [
    { key: 'device', label: 'deviceTitle', icon: DevicesIcon, endpoint: '/api/devices?all=true&excludeAttributes=true', keyLink: 'deviceId', keyBase: 'userId', titleGetter: 'device' },
    { key: 'group', label: 'settingsGroups', icon: FolderIcon, endpoint: '/api/groups?all=true', keyLink: 'groupId', keyBase: 'userId' },
    { key: 'geofence', label: 'sharedGeofences', icon: PlaceIcon, endpoint: '/api/geofences?all=true', keyLink: 'geofenceId', keyBase: 'userId' },
    { key: 'notification', label: 'sharedNotifications', icon: NotificationsIcon, endpoint: '/api/notifications?all=true', keyLink: 'notificationId', keyBase: 'userId', titleGetter: 'notification' },
    { key: 'calendar', label: 'sharedCalendars', icon: CalendarTodayIcon, endpoint: '/api/calendars?all=true', keyLink: 'calendarId', keyBase: 'userId' },
    { key: 'managedUser', label: 'settingsUsers', icon: PeopleIcon, endpoint: '/api/users?all=true&excludeAttributes=true', keyLink: 'managedUserId', keyBase: 'userId' },
    { key: 'driver', label: 'sharedDrivers', icon: PersonIcon, endpoint: '/api/drivers?all=true', keyLink: 'driverId', keyBase: 'userId', titleGetter: 'driver' },
    { key: 'command', label: 'sharedSavedCommands', icon: TerminalIcon, endpoint: '/api/commands?all=true', keyLink: 'commandId', keyBase: 'userId', titleGetter: 'description' },
    { key: 'maintenance', label: 'sharedMaintenance', icon: BuildIcon, endpoint: '/api/maintenance?all=true', keyLink: 'maintenanceId', keyBase: 'userId' },
  ],
};

// Helper para obter título de item
const getItemTitle = (item, titleGetter, t) => {
  if (!titleGetter) return item.name;
  if (titleGetter === 'notification') return formatNotificationTitle(t, item);
  if (titleGetter === 'driver') return `${item.name} (${item.uniqueId})`;
  if (titleGetter === 'device') return `${item.name} (${item.uniqueId})`;
  if (titleGetter === 'description') return item.description;
  return item.name;
};

const BulkConnectionsDialog = memo(({
  open,
  onClose,
  selectedIds = [],
  entityType = 'device',
  entityName = 'itens',
  onSuccess,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const features = useFeatures();

  // State
  const [connectionType, setConnectionType] = useState('');
  const [action, setAction] = useState('link'); // 'link' or 'unlink'
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Get available connection types for entity
  const connectionTypes = useMemo(() => {
    const configs = CONNECTION_CONFIGS[entityType] || [];
    return configs.filter((config) => {
      if (config.feature && features[config.feature]) return false;
      return true;
    });
  }, [entityType, features]);

  // Get current connection config
  const currentConfig = useMemo(() => (
    connectionTypes.find((c) => c.key === connectionType)
  ), [connectionTypes, connectionType]);

  // Reset state when dialog opens/closes
  const handleClose = useCallback(() => {
    setConnectionType('');
    setAction('link');
    setAvailableItems([]);
    setSelectedItems([]);
    setError(null);
    setSuccess(false);
    onClose();
  }, [onClose]);

  // Fetch available items when connection type changes
  useEffectAsync(async () => {
    if (!currentConfig || !open) return;

    setLoadingItems(true);
    setAvailableItems([]);
    setSelectedItems([]);
    setError(null);

    try {
      const response = await fetchOrThrow(currentConfig.endpoint);
      const items = await response.json();
      setAvailableItems(items);
    } catch (err) {
      setError(t('sharedLoadingError') || 'Erro ao carregar itens');
    } finally {
      setLoadingItems(false);
    }
  }, [currentConfig, open]);

  // Handle apply bulk connection
  const handleApply = useCallback(async () => {
    if (!currentConfig || selectedItems.length === 0 || selectedIds.length === 0) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const method = action === 'link' ? 'POST' : 'DELETE';
      const requests = [];

      // Create requests for each combination of baseId and linkId
      selectedIds.forEach((baseId) => {
        selectedItems.forEach((item) => {
          const body = {};
          body[currentConfig.keyBase] = Number(baseId);
          body[currentConfig.keyLink] = item.id;

          requests.push(
            fetchOrThrow('/api/permissions', {
              method,
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            }).catch(() => null) // Ignore individual errors (e.g., already linked/unlinked)
          );
        });
      });

      await Promise.all(requests);

      setSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
        handleClose();
      }, 1000);
    } catch (err) {
      setError(t('sharedError') || 'Erro ao aplicar conexões');
    } finally {
      setLoading(false);
    }
  }, [currentConfig, selectedItems, selectedIds, action, onSuccess, handleClose, t]);

  // Colors with fallbacks
  const colors = {
    surface: tokens?.surface?.elevated || '#161b22',
    border: tokens?.border?.subtle || 'rgba(255,255,255,0.1)',
    textPrimary: tokens?.text?.primary || '#f0f6fc',
    textSecondary: tokens?.text?.secondary || '#8b949e',
    accent: tokens?.accent?.primary || '#00d4aa',
    success: tokens?.semantic?.success || '#3fb950',
    error: tokens?.semantic?.error || '#f85149',
  };

  const totalOperations = selectedIds.length * selectedItems.length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: colors.surface,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.border}`,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: colors.textPrimary,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${colors.accent}20`,
          }}
        >
          <LinkIcon sx={{ color: colors.accent }} />
        </Box>
        {t('sharedConnections') || 'Conexões em Massa'}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Info chip */}
        <Chip
          label={`${selectedIds.length} ${entityName} selecionado(s)`}
          size="small"
          sx={{
            mb: 2,
            bgcolor: `${colors.accent}20`,
            color: colors.accent,
            fontWeight: 600,
          }}
        />

        {/* Action toggle */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
            {t('sharedAction') || 'Ação'}
          </Typography>
          <ToggleButtonGroup
            value={action}
            exclusive
            onChange={(_, value) => value && setAction(value)}
            size="small"
            fullWidth
          >
            <ToggleButton
              value="link"
              sx={{
                color: action === 'link' ? colors.success : colors.textSecondary,
                borderColor: action === 'link' ? colors.success : colors.border,
                '&.Mui-selected': {
                  bgcolor: `${colors.success}20`,
                  color: colors.success,
                  '&:hover': { bgcolor: `${colors.success}30` },
                },
              }}
            >
              <LinkIcon sx={{ mr: 1 }} />
              {t('sharedLink') || 'Vincular'}
            </ToggleButton>
            <ToggleButton
              value="unlink"
              sx={{
                color: action === 'unlink' ? colors.error : colors.textSecondary,
                borderColor: action === 'unlink' ? colors.error : colors.border,
                '&.Mui-selected': {
                  bgcolor: `${colors.error}20`,
                  color: colors.error,
                  '&:hover': { bgcolor: `${colors.error}30` },
                },
              }}
            >
              <LinkOffIcon sx={{ mr: 1 }} />
              {t('sharedUnlink') || 'Desvincular'}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Connection type selector */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel sx={{ color: colors.textSecondary }}>
            {t('sharedType') || 'Tipo de Conexão'}
          </InputLabel>
          <Select
            value={connectionType}
            onChange={(e) => setConnectionType(e.target.value)}
            label={t('sharedType') || 'Tipo de Conexão'}
            sx={{
              color: colors.textPrimary,
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.accent,
              },
            }}
          >
            {connectionTypes.map((config) => {
              const Icon = config.icon;
              return (
                <MenuItem key={config.key} value={config.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon sx={{ fontSize: 20, color: colors.accent }} />
                    {t(config.label)}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* Items selector */}
        {connectionType && (
          <Autocomplete
            multiple
            loading={loadingItems}
            options={availableItems}
            value={selectedItems}
            onChange={(_, value) => setSelectedItems(value)}
            getOptionLabel={(item) => getItemTitle(item, currentConfig?.titleGetter, t)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <Box component="li" key={option.id} {...rest}>
                  {getItemTitle(option, currentConfig?.titleGetter, t)}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(currentConfig?.label) || 'Selecionar'}
                placeholder={loadingItems ? t('sharedLoading') : t('sharedSelect')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: colors.textPrimary,
                    '& fieldset': { borderColor: colors.border },
                    '&:hover fieldset': { borderColor: colors.accent },
                  },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={getItemTitle(option, currentConfig?.titleGetter, t)}
                  size="small"
                  sx={{
                    bgcolor: `${colors.accent}20`,
                    color: colors.accent,
                  }}
                />
              ))
            }
            sx={{ mb: 2 }}
          />
        )}

        {/* Summary */}
        {selectedItems.length > 0 && (
          <Alert
            severity={action === 'link' ? 'info' : 'warning'}
            sx={{
              bgcolor: action === 'link' ? `${colors.success}15` : `${colors.error}15`,
              color: colors.textPrimary,
              '& .MuiAlert-icon': {
                color: action === 'link' ? colors.success : colors.error,
              },
            }}
          >
            {action === 'link'
              ? `Serão criadas ${totalOperations} conexões`
              : `Serão removidas até ${totalOperations} conexões`
            }
          </Alert>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success */}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('sharedSaved') || 'Conexões aplicadas com sucesso!'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ color: colors.textSecondary }}
        >
          {t('sharedCancel') || 'Cancelar'}
        </Button>
        <Button
          onClick={handleApply}
          disabled={loading || selectedItems.length === 0 || !connectionType}
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : (action === 'link' ? <LinkIcon /> : <LinkOffIcon />)}
          sx={{
            bgcolor: action === 'link' ? colors.success : colors.error,
            color: '#ffffff',
            minWidth: 120,
            '&:hover': {
              bgcolor: action === 'link' ? '#2ea043' : '#da3633',
            },
          }}
        >
          {loading
            ? (t('sharedLoading') || 'Aplicando...')
            : (action === 'link' ? (t('sharedLink') || 'Vincular') : (t('sharedUnlink') || 'Desvincular'))
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
});

BulkConnectionsDialog.displayName = 'BulkConnectionsDialog';

export default BulkConnectionsDialog;
